import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { loanService } from '../../services/loanService';
import { LoanStatusBadge } from '../../components/common/StatusBadge';
import { formatCurrency, formatDate } from '../../utils/helpers';
import LoadingSpinner from '../../components/common/LoadingSpinner';

// ── Offer accept/decline modal ────────────────────────────────────────────────

function OfferModal({ loan, onClose, onAccept, onDecline, isPending }) {
  const offer = loan.loan_offer;
  if (!offer) return null;

  const rows = [
    ['Approved Amount',    formatCurrency(offer.approved_amount)],
    ['Interest Rate',      `${offer.interest_rate}% p.m.`],
    ['Tenure',             `${offer.tenure_months} months`],
    ['Monthly Installment',formatCurrency(offer.monthly_installment)],
    ['Total Repayment',    formatCurrency(offer.total_repayment)],
    ['Total Interest',     formatCurrency(offer.total_interest)],
    ['Processing Fee',     formatCurrency(offer.processing_fee)],
    ['Net Disbursement',   formatCurrency(offer.net_disbursement)],
    ...(offer.late_penalty_type ? [
      ['Late Penalty', offer.late_penalty_type === 'fixed'
        ? formatCurrency(offer.late_penalty_value)
        : `${offer.late_penalty_value}% of overdue`],
      ['Default Charge', `${offer.default_charge_rate}% p.m. after ${offer.default_threshold_days} days`],
    ] : []),
    ['Offer Expires', formatDate(offer.expires_at)],
  ];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div className="card" style={{ width: 520, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
          <div>
            <h2 style={{ fontWeight: 700, fontSize: '1.1rem' }}>Loan Offer</h2>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginTop: 2 }}>{loan.application_number}</p>
          </div>
          <button className="btn btn-ghost" style={{ padding: '0.25rem 0.5rem' }} onClick={onClose}>✕</button>
        </div>

        <div className="alert alert-info" style={{ marginBottom: '1rem' }}>
          <span>⏱</span>
          <span style={{ fontSize: '0.8rem' }}>Offer expires on <strong>{formatDate(offer.expires_at)}</strong>. Accept before expiry to secure your loan.</span>
        </div>

        <div style={{ background: 'var(--surface-alt)', borderRadius: 'var(--radius)', padding: '1rem', marginBottom: '1.25rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.875rem' }}>
            {rows.map(([label, val]) => (
              <div key={label}>
                <div style={{ fontSize: '0.65rem', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 2 }}>{label}</div>
                <div style={{ fontSize: '0.9rem', fontWeight: 600, color: label.includes('Net') ? '#00C896' : label.includes('Interest') || label.includes('Fee') ? '#F5A623' : 'var(--text-1)' }}>{val}</div>
              </div>
            ))}
          </div>
        </div>

        {offer.offer_terms && (
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ fontSize: '0.65rem', color: 'var(--text-3)', textTransform: 'uppercase', marginBottom: '0.4rem' }}>Terms & Conditions</div>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-2)', lineHeight: 1.6 }}>{offer.offer_terms}</p>
          </div>
        )}

        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button className="btn btn-primary" style={{ flex: 2 }} disabled={isPending} onClick={onAccept}>
            {isPending ? 'Processing…' : 'Accept Offer'}
          </button>
          <button className="btn btn-ghost" style={{ flex: 1, color: '#FF4757', borderColor: '#FF475740' }} disabled={isPending} onClick={onDecline}>
            Decline
          </button>
          <button className="btn btn-ghost" style={{ flex: 1 }} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

const PENDING_STATUSES = ['submitted', 'under_review', 'credit_check', 'offer_sent', 'offer_accepted'];

export default function LoanStatus() {
  const location = useLocation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [msg, setMsg] = useState(location.state?.success || '');
  const [offerLoan, setOfferLoan] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['my-loans'],
    queryFn: () => loanService.getMyLoans().then(r => r.data.data),
  });

  const acceptMutation = useMutation({
    mutationFn: (id) => loanService.acceptOffer(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-loans'] });
      setMsg('Offer accepted! Disbursement will be processed within 24–48 hours.');
      setOfferLoan(null);
    },
  });

  const declineMutation = useMutation({
    mutationFn: (id) => loanService.declineOffer(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['my-loans'] }); setOfferLoan(null); },
  });

  const loans = data?.results || [];
  const isPending = acceptMutation.isPending || declineMutation.isPending;
  const hasPendingLoan = loans.some(l => PENDING_STATUSES.includes(l.status));

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>My Loan Applications</h1>
          <p>Track the status of all your loan applications</p>
        </div>
        {hasPendingLoan ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', background: 'rgba(245,166,35,0.1)', border: '1px solid rgba(245,166,35,0.25)', borderRadius: 'var(--radius)', fontSize: '0.8rem', color: '#F5A623', fontWeight: 600 }}>
            ⏳ Pending application in progress
          </div>
        ) : (
          <Link to="/loans/apply" className="btn btn-primary">New Application</Link>
        )}
      </div>

      {msg && (
        <div className="alert alert-success" style={{ marginBottom: '1rem' }}>
          <span>✓</span><span>{msg}</span>
        </div>
      )}

      {isLoading ? <LoadingSpinner /> : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Application #</th>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Tenure</th>
                  <th>Status</th>
                  <th>Submitted</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loans.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-3)', padding: '2rem' }}>
                      No applications yet. <Link to="/loans/apply" style={{ color: 'var(--primary)' }}>Apply now →</Link>
                    </td>
                  </tr>
                ) : loans.map(loan => (
                  <tr key={loan.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/loans/${loan.id}`)}>
                    <td style={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.8rem' }}>{loan.application_number}</td>
                    <td style={{ textTransform: 'capitalize', fontSize: '0.875rem' }}>{loan.loan_type?.replace(/_/g, ' ')}</td>
                    <td style={{ fontWeight: 600 }}>{formatCurrency(loan.approved_amount ?? loan.requested_amount)}</td>
                    <td style={{ fontSize: '0.875rem' }}>{loan.requested_tenure_months}m</td>
                    <td><LoanStatusBadge status={loan.status} /></td>
                    <td style={{ fontSize: '0.8rem', color: 'var(--text-2)' }}>{formatDate(loan.submitted_at)}</td>
                    <td onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'nowrap' }}>
                        <Link
                          to={`/loans/${loan.id}`}
                          className="btn btn-ghost"
                          style={{ fontSize: '0.75rem', padding: '0.3rem 0.75rem' }}>
                          View Details
                        </Link>
                        {loan.status === 'offer_sent' && (
                          <button
                            className="btn btn-primary"
                            style={{ fontSize: '0.75rem', padding: '0.3rem 0.75rem', whiteSpace: 'nowrap' }}
                            onClick={() => setOfferLoan(loan)}>
                            View Offer
                          </button>
                        )}
                        {(loan.status === 'active' || loan.status === 'closed' || loan.status === 'defaulted') && (
                          <Link
                            to={`/loans/${loan.id}/repayments`}
                            className="btn btn-ghost"
                            style={{ fontSize: '0.75rem', padding: '0.3rem 0.75rem' }}>
                            Schedule
                          </Link>
                        )}
                        {loan.status === 'rejected' && loan.rejection_reason && (
                          <span style={{ fontSize: '0.7rem', color: '#FF4757', maxWidth: 140, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {loan.rejection_reason}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {offerLoan && (
        <OfferModal
          loan={offerLoan}
          isPending={isPending}
          onClose={() => setOfferLoan(null)}
          onAccept={() => acceptMutation.mutate(offerLoan.id)}
          onDecline={() => declineMutation.mutate(offerLoan.id)}
        />
      )}
    </div>
  );
}
